import { max, min } from 'date-fns';

import { CreateCompanyFormValues } from '~/types/formValues';
import { differenceInMonths } from '~/utils/date';
import { ERROR_MESSAGES } from '~/utils/formSchemas';

export const calculateAcquired = (
  dealSize: string | number,
  postMoneyValuation: string | number,
) => {
  const acquired =
    (typeof dealSize === 'number' ? dealSize : parseFloat(dealSize)) /
    (typeof postMoneyValuation === 'number' ? postMoneyValuation : parseFloat(postMoneyValuation));

  return Number.isFinite(acquired) ? acquired : 0;
};

export const calculateCompanyFilledPercent = (formValues: CreateCompanyFormValues['form']) => {
  const {
    name,
    web,
    foundedYear,
    industries,
    firstFinancingDate,
    firstFinancingDealType,
    competitors,
    rounds,
  } = formValues;

  let totalRequiredFieldsCount = 0;
  let filledRequiredFieldsCount = 0;

  const requiredFieldValuesInGeneralInfo = [
    name,
    web,
    foundedYear,
    industries.length,
    firstFinancingDate,
    firstFinancingDealType,
    competitors.length,
  ];

  totalRequiredFieldsCount += requiredFieldValuesInGeneralInfo.length;
  requiredFieldValuesInGeneralInfo.forEach(value => {
    if (value) {
      filledRequiredFieldsCount++;
    }
  });

  rounds.forEach((round, roundIndex) => {
    round.subrounds.forEach(subround => {
      const {
        dealDate,
        announcedDate,
        dealSize,
        preMoneyValuation,
        postMoneyValuation,
        acquiredPercent,
        employees,
        ceoChanged,
        ceo,
        investors,
        securityType,
        cap,
        discount,
        interest,
        noAnnouncement,
      } = subround;

      const requiredFieldValuesInSubround = [
        dealDate,
        dealSize,
        preMoneyValuation,
        postMoneyValuation,
        acquiredPercent,
        employees,
        investors.length,
      ];

      if (!noAnnouncement) {
        requiredFieldValuesInSubround.push(announcedDate);
      }

      totalRequiredFieldsCount += requiredFieldValuesInSubround.length;
      requiredFieldValuesInSubround.forEach(value => {
        if (value) {
          filledRequiredFieldsCount++;
        }
      });

      if (roundIndex === 0 || ceoChanged) {
        totalRequiredFieldsCount += 2;

        if (ceo.name) {
          filledRequiredFieldsCount++;
        }

        if (ceo.education) {
          filledRequiredFieldsCount++;
        }
      }

      if (roundIndex === 2) {
        totalRequiredFieldsCount += 1;

        if (securityType) {
          filledRequiredFieldsCount++;

          if (securityType.value === 2 || securityType.value === 3) {
            totalRequiredFieldsCount += 2;

            if (cap) {
              filledRequiredFieldsCount++;
            }

            if (discount) {
              filledRequiredFieldsCount++;
            }
          }

          if (securityType.value === 3) {
            totalRequiredFieldsCount += 1;

            if (interest) {
              filledRequiredFieldsCount++;
            }
          }
        }
      }
    });
  });

  return Math.trunc((filledRequiredFieldsCount / totalRequiredFieldsCount) * 100);
};

export const calculateRoundSummary = (
  round: CreateCompanyFormValues['form']['rounds'][number],
  firstFinancingDate: Date | null,
) => {
  let isValid = true;
  const errors: Record<string, string> = {};
  const lastSubround = round.subrounds[round.subrounds.length - 1];
  const totalDealSize = round.subrounds
    .reduce((acc, subround) => acc + parseFloat(subround.dealSize || '0.00'), 0)
    .toFixed(2);

  const calculateShareSold = (calculatedShareSold = 0, currentSubroundIndex = 0): number => {
    if (round.subrounds.length === currentSubroundIndex) {
      return calculatedShareSold;
    }

    const acquiredCalc = parseFloat(round.subrounds[currentSubroundIndex].acquiredCalc) || 0;

    if (currentSubroundIndex === 0) {
      return calculateShareSold(acquiredCalc, ++currentSubroundIndex);
    }

    return calculateShareSold(
      calculatedShareSold * (1 - acquiredCalc) + acquiredCalc,
      ++currentSubroundIndex,
    );
  };

  const shareSold = calculateShareSold();
  const postMoney = shareSold ? parseFloat(totalDealSize) / shareSold : 0;
  const preMoney = postMoney - parseFloat(totalDealSize);
  const allDealDates = round.subrounds.map(subround => subround.dealDate).filter(Boolean) as Date[];
  const maxDealDate = allDealDates.length ? max(allDealDates) : null;
  const allAnnouncedDates = round.subrounds
    .map(subround => subround.announcedDate)
    .filter(Boolean) as Date[];
  const minAnnouncedDate = allAnnouncedDates.length ? min(allAnnouncedDates) : null;

  const uniqueInvestors: CreateCompanyFormValues['form']['rounds'][number]['subrounds'][number]['investors'] =
    [];
  const investors = round.subrounds.map(subround => subround.investors).flat();
  investors.forEach(investor => {
    const existingInvestorIndex = uniqueInvestors.findIndex(
      uniqueInvestor => uniqueInvestor.id === investor.id,
    );

    if (existingInvestorIndex === -1) {
      uniqueInvestors.push({ ...investor });
    } else {
      if (investor.corporate) {
        uniqueInvestors[existingInvestorIndex].corporate = true;
      }

      if (investor.lead) {
        uniqueInvestors[existingInvestorIndex].lead = true;
      }

      if (investor.new) {
        uniqueInvestors[existingInvestorIndex].new = true;
      }
    }
  });

  if (preMoney === 0) {
    isValid = false;

    errors.preMoney = ERROR_MESSAGES.INVALID_POSITIVE_MONEY;
  }

  return {
    isValid,
    errors,
    data: {
      totalEmployees: +lastSubround.employees || 0,
      totalInvestors: uniqueInvestors.length,
      totalNewInvestors: uniqueInvestors.filter(investor => investor.new).length,
      totalDealSize: +totalDealSize,
      shareSold,
      postMoney,
      preMoney,
      monthsFromFirstFinancing:
        lastSubround.dealDate && firstFinancingDate
          ? differenceInMonths(lastSubround.dealDate, firstFinancingDate)
          : 0,
      monthsBetweenAnnouncementAndFinalClose:
        maxDealDate && minAnnouncedDate ? differenceInMonths(maxDealDate, minAnnouncedDate) : 0,
      monthsBetweenClosings:
        round.subrounds.length > 1 && round.subrounds[0].dealDate && lastSubround.dealDate
          ? differenceInMonths(lastSubround.dealDate, round.subrounds[0].dealDate)
          : 0,
      dealDate: maxDealDate,
      announcedDate: minAnnouncedDate,
    },
  };
};

export const calculateInvestorTotalFieldCount = (
  investors: CreateCompanyFormValues['form']['rounds'][number]['subrounds'][number]['investors'],
  field: keyof CreateCompanyFormValues['form']['rounds'][number]['subrounds'][number]['investors'][number],
) => {
  return investors.reduce((acc, investor) => {
    return investor[field] ? acc + 1 : acc;
  }, 0);
};
